import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Snackbar } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import { IsMobile } from "utils/is-mobile";

import { DialogProductDetailShowcaseClient } from "./components/product-detail";
import BrandSelector from "./components/brand-selector";
import VitrineHeader from "./components/vitrine-header";
import ProductCard from "./components/product-card";
import CartSidebar from "./components/cart-sidebar";
import CartButton from "./components/cart-button";
import SpecsModal from "./components/specs-modal";
import ConfirmationModal from "./components/confirmation-modal";
import PersonalDataForm from "./components/personal-data-form";
import ErrorMessages from "./components/error-messages";
import useCartManager from "./hooks/useCartManager";

const getBrandColors = (selectedBrand) => {
  switch (selectedBrand) {
    case "trussardi":
      return {
        primary: "#B7A99A",
        primaryHover: "#A98B6C",
        bg: "#F4F4F4",
        surface: "#FFFFFF",
        textBase: "#000000",
        textMuted: "#6B6B6B",
        border: "#D4D3D3",
      };
    case "karsten-decor":
      return {
        primary: "#005870",
        primaryHover: "#004a5e",
        bg: "#F4F4F4",
        surface: "#FFFFFF",
        textBase: "#3C3C3B",
        textMuted: "#878787",
        border: "#D4D3D3",
      };
    case "mix":
      return {
        primary: "#222222",
        primaryHover: "#333333",
        bg: "#F4F4F4",
        surface: "#FFFFFF",
        textBase: "#222222",
        textMuted: "#777777",
        border: "#D4D3D3",
        karstenAccent: "#005870",
        trussardiAccent: "#B7A99A",
      };
    case "karsten":
    default:
      return {
        primary: "#005870",
        primaryHover: "#004a5e",
        bg: "#F4F4F4",
        surface: "#FFFFFF",
        textBase: "#3C3C3B",
        textMuted: "#878787",
        border: "#D4D3D3",
      };
  }
};

function setProductsToStorage(hash, products) {
  localStorage.setItem("vitrine_products", JSON.stringify({ hash, products }));

  const allProductsData = localStorage.getItem("vitrine_all_products");
  const allProducts = allProductsData ? JSON.parse(allProductsData) : {};

  products.forEach((product) => {
    allProducts[product.idProduto] = product;
  });

  localStorage.setItem("vitrine_all_products", JSON.stringify(allProducts));
}

function getAllProductsFromStorage() {
  try {
    const allProductsData = localStorage.getItem("vitrine_all_products");
    return allProductsData ? JSON.parse(allProductsData) : {};
  } catch (error) {
    return {};
  }
}

function clearProductsStorage() {
  localStorage.removeItem("vitrine_products");
}

const useStyles = makeStyles(() => ({
  karstenLayout: {
    "&": {
      "--karsten-azul": "#005870",
      "--karsten-turquesa": "#92CFCA",
      "--karsten-grafite": "#3C3C3B",
      "--karsten-areia": "#D4D3D3",
      "--karsten-cinza": "#878787",
      "--karsten-bg": "#F4F4F4",
      "--karsten-surface": "#FFFFFF",
    },
    "& .bg-brand": {
      backgroundColor: "#005870",
    },
    "& .hover\\:bg-brand-hover:hover": {
      backgroundColor: "#004a5e",
    },
    "& .text-brand": {
      color: "#005870",
    },
    "& .text-accent": {
      color: "#92CFCA",
    },
    "& .border-brand": {
      borderColor: "#005870",
    },
    "& .font-display": {
      fontFamily: "'Lora', serif",
    },
  },
  trussardiLayout: {
    "&": {
      "--trussardi-bg": "#FFFFFF",
      "--trussardi-surface": "#FFFFFF",
      "--trussardi-primary": "#B7A99A",
      "--trussardi-primary-hover": "#A98B6C",
      "--trussardi-text-base": "#000000",
      "--trussardi-text-muted": "#6B6B6B",
      "--trussardi-border": "#EAEAEA",
    },
    "& .bg-brand": {
      backgroundColor: "#B7A99A",
    },
    "& .hover\\:bg-brand-hover:hover": {
      backgroundColor: "#A98B6C",
    },
    "& .text-brand": {
      color: "#B7A99A",
    },
    "& .surface": {
      backgroundColor: "#FFFFFF",
    },
    "& .text-muted": {
      color: "#6B6B6B",
    },
    "& .border-color": {
      borderColor: "#EAEAEA",
    },
    "& .font-title": {
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 500,
    },
  },
  mixLayout: {
    "&": {
      "--mix-bg": "#F8F8F8",
      "--mix-surface": "#FFFFFF",
      "--mix-text-base": "#222222",
      "--mix-text-muted": "#777777",
      "--mix-border": "#E0E0E0",
      "--mix-karsten-accent": "#005870",
      "--mix-trussardi-accent": "#B7A99A",
    },
    "& .surface": {
      backgroundColor: "#FFFFFF",
    },
    "& .text-muted": {
      color: "#777777",
    },
    "& .border-color": {
      borderColor: "#E0E0E0",
    },
  },
  cartClosed: {
    transform: "translateX(100%)",
  },
  cartOpen: {
    transform: "translateX(0)",
  },
  scrollHidden: {
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  imageCarousel: {
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  numberInput: {
    "&::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "&::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    MozAppearance: "textfield",
  },
}));

export default function VitrineVirtualClientes() {
  const classes = useStyles();
  const mobile = IsMobile();

  const [data, setData] = useState(null);
  const {
    cart,
    addToCart: addToCartManager,
    removeFromCart: removeFromCartManager,
    updateCartQuantity: updateCartQuantityManager,
    clearCart,
    getTotalItems,
    validateCartItems,
  } = useCartManager(data?.hashVitrine);
  const [openDetail, setOpenDetail] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [searching, setSearching] = useState(false);
  const [openMessageError, setOpenMessageError] = useState(false);
  const [finishOrder, setFinishOrder] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [personalData, setPersonalData] = useState(false);
  const [cpfInput, setCpfInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [personalDataError, setPersonalDataError] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("karsten");
  const [availableBrands, setAvailableBrands] = useState([]);
  const [openSpecsModal, setOpenSpecsModal] = useState(false);
  const [specsProduct, setSpecsProduct] = useState(null);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const processApiResponse = (response) => {
    const hash = response.Result?.HashVitrine;
    const showcase = response.Result?.VitrineVirtualConsumidorEnvio;
    const products = (showcase?.Itens || []).map((item) => {
      const precoAnterior =
        item?.PrecoPromo === 0 || item?.PrecoPromo === item?.Preco
          ? null
          : item?.PrecoFormat;
      const preco =
        item?.PrecoPromo === 0 || item?.PrecoPromo === item?.Preco
          ? item?.PrecoFormat
          : item?.PrecoPromoFormat;
      const precoAnteriorNum = precoAnterior
        ? parseFloat(precoAnterior.replace(/[^\d,]/g, "").replace(",", "."))
        : null;
      const precoNum = parseFloat(
        preco.replace(/[^\d,]/g, "").replace(",", ".")
      );
      const desconto =
        precoAnteriorNum && precoNum && precoAnteriorNum > precoNum
          ? Math.round(((precoAnteriorNum - precoNum) / precoAnteriorNum) * 100)
          : null;

      return {
        fotos: item?.Fotos || [],
        descricao: item?.DescricaoProduto || "",
        idProduto: item?.IdProduto?.toString() || "",
        name: item?.DescricaoProduto || "",
        precoAnterior: precoAnterior,
        preco: preco,
        desconto: desconto,
        codigo: item?.IdProduto?.toString() || "",
        unidadeMedida: item?.UnidadeMedida || "UN",
        marcaId: item?.IdMarca,
        specs: item?.Especificacoes || "",
      };
    });

    return {
      id: showcase?.IdVitrineVirtual,
      hashVitrine: hash,
      products,
      nameVitrine: showcase?.NomeVitrine || showcase?.Nome || "",
      store: showcase?.Loja || "",
      vendedor: showcase?.Vendedor || "",
      idVendedor: showcase?.IdVendedor || response.Result?.IdLojaVendedor,
      storeId: showcase?.IdPessoaLoja || response.Result?.IdPessoaLoja,
      brandId: showcase?.IdMarcaKarsten,
      isExclusive: !showcase?.VitrinePublica,
      nameConsumidor: showcase?.NomeConsumidor || "",
      cpf: showcase?.Cpf || "",
    };
  };

  const handleGetClientShowcase = async (client) => {
    setSearching(true);
    setOpenMessageError(false);

    try {
      const response = await Api.get(
        "virtual-customer-detail",
        "showcase",
        `?hashVitrine=${client}`,
        false
      );
      const processedData = processApiResponse(response);
      setData(processedData);
    } catch (error) {
      setOpenMessageError(true);
    } finally {
      setSearching(false);
    }
  };

  const getHashFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);

    let hashVitrine = searchParams.get("hashVitrine");

    if (!hashVitrine) {
      const queryString = window.location.search.substring(1);
      const params = queryString.split("&");

      for (const param of params) {
        if (!param) continue;

        if (!param.includes("=")) {
          hashVitrine = param;
          break;
        }

        const [key, value] = param.split("=");
        if (key && key !== "cpf" && value) {
          hashVitrine = value;
          break;
        }
      }
    }

    return hashVitrine;
  };

  useEffect(() => {
    const hashVitrine = getHashFromUrl();
    if (hashVitrine) {
      handleGetClientShowcase(hashVitrine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const brandIds = new Set();

    if (data && data.products && data.products.length > 0) {
      data.products.forEach((product) => {
        if (product.marcaId) {
          brandIds.add(product.marcaId);
        }
      });
    }

    const hasKarsten = brandIds.has(1) || brandIds.has(3);
    const hasTrussardi = brandIds.has(2);
    const hasMultipleBrands = hasKarsten && hasTrussardi;

    const available = [
      {
        id: 1,
        name: "karsten",
        label: "Karsten",
        disabled: !hasKarsten,
      },
      {
        id: 2,
        name: "trussardi",
        label: "Trussardi",
        disabled: !hasTrussardi,
      },
      {
        id: "mix",
        name: "mix",
        label: "Mix",
        disabled: !hasMultipleBrands,
      },
    ];

    setAvailableBrands(available);

    if (available.length > 0) {
      const currentBrandId =
        selectedBrand === "karsten"
          ? 1
          : selectedBrand === "trussardi"
          ? 2
          : selectedBrand === "mix"
          ? "mix"
          : null;
      const currentBrand = available.find((b) => b.id === currentBrandId);
      const isCurrentBrandAvailable = currentBrand && !currentBrand.disabled;

      if (!isCurrentBrandAvailable) {
        const firstEnabledBrand = available.find((b) => !b.disabled);
        if (firstEnabledBrand) {
          setSelectedBrand(firstEnabledBrand.name);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.products, selectedBrand]);

  useEffect(() => {
    if (!data || !data.products) return;
    setProductsToStorage(data.id, data.products);

    const allProducts = getAllProductsFromStorage();
    const allProductsArray = Object.values(allProducts);

    if (allProductsArray.length > 0) {
      validateCartItems(allProductsArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.hashVitrine]);

  useEffect(() => {
    if (finishOrder === "success") {
      clearCart();
      clearProductsStorage();
      setOpenConfirmationModal(true);
    }
  }, [finishOrder, clearCart]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isCartOpen]);

  useEffect(() => {
    if (!selectedBrand) return;

    const brandColors = getBrandColors(selectedBrand);
    const bgColor = brandColors.bg;
    document.body.style.backgroundColor = bgColor;
    document.documentElement.style.backgroundColor = bgColor;

    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [selectedBrand]);

  function getCartDetails() {
    if (!data) return [];

    const allProducts = getAllProductsFromStorage();

    return cart
      .map((item) => {
        let prod = data.products.find((p) => p.idProduto === item.idProduto);

        if (!prod && allProducts[item.idProduto]) {
          prod = allProducts[item.idProduto];
        }

        return prod ? { ...prod, quantidade: item.quantidade } : null;
      })
      .filter(Boolean);
  }

  const addToCart = (idProduto, quantidade = 1) => {
    const product = data?.products?.find((p) => p.idProduto === idProduto);
    const productName = product?.name || "Item";

    addToCartManager(idProduto, quantidade);
    setProductQuantities((prev) => ({ ...prev, [idProduto]: 0 }));

    setSnackbarMessage(`Item "${productName}" adicionado ao carrinho`);
    setSnackbarOpen(true);
  };

  const removeFromCart = (idProduto) => {
    removeFromCartManager(idProduto);
  };

  const updateCartQuantity = (idProduto, quantidade) => {
    updateCartQuantityManager(idProduto, quantidade);
  };

  const handleFinishOrder = async () => {
    const cpf = data?.cpf || cpfInput.replace(/[^\d]+/g, "");
    const nameConsumidor = data?.nameConsumidor || nameInput;

    setIsCartOpen(false);

    if (!cpf || !nameConsumidor) {
      setPersonalData(true);
      setCpfInput(data?.cpf || "");
      setNameInput(data?.nameConsumidor || "");
      return;
    }

    const params = {
      tokenVitrine: data?.hashVitrine,
      idLojaVendedor: data?.idVendedor,
      IdPessoaLoja: data?.storeId,
      nomeConsumidor: nameConsumidor,
      cpf: cpf,
      jsonProdutos: cart,
      statusCarrinho: "CONFIRMADO",
    };

    try {
      await Api.post(
        "edit-item-virtual-customer",
        "showcase",
        {
          jsonVitrine: params,
        },
        true
      );
      setFinishOrder("success");
    } catch (error) {
      setFinishOrder("error");
    }
  };

  const handlePersonalDataSubmit = () => {
    if (!nameInput || nameInput.trim().length < 2) {
      setPersonalDataError("Nome obrigatório");
      return;
    }
    if (!MaskAndValid.validateCPF(cpfInput)) {
      setPersonalDataError("CPF inválido");
      return;
    }
    setPersonalData(false);
    setPersonalDataError("");
    setTimeout(() => {
      handleFinishOrder();
    }, 0);
  };

  const brandColors = getBrandColors(selectedBrand);

  const handleQuantityChange = (productId, delta) => {
    setProductQuantities((prev) => {
      const current = prev[productId] || 1;
      const newValue = Math.max(0, current + delta);
      return { ...prev, [productId]: newValue };
    });
  };

  const handleCarouselScroll = (productId, direction) => {
    const carousel = document.querySelector(
      `[data-product-id="${productId}"] [data-carousel="true"]`
    );
    if (carousel) {
      const scrollAmount =
        direction === "next" ? carousel.clientWidth : -carousel.clientWidth;
      carousel.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleShowSpecs = (product) => {
    setSpecsProduct(product);
    setOpenSpecsModal(true);
  };

  const cartDetails = getCartDetails();
  const totalItems = getTotalItems();

  let subtotal = 0;
  let total = 0;
  let discount = 0;

  cartDetails.forEach((item) => {
    const precoAnterior = item.precoAnterior
      ? parseFloat(item.precoAnterior.replace(",", "."))
      : parseFloat(item.preco.replace(",", "."));
    const preco = parseFloat(item.preco.replace(",", "."));
    subtotal += precoAnterior * item.quantidade;
    total += preco * item.quantidade;
  });
  discount = subtotal - total;

  const isKarstenLayout = selectedBrand === "karsten";
  const isTrussardiLayout = selectedBrand === "trussardi";

  return (
    <div
      className={
        isKarstenLayout
          ? classes.karstenLayout
          : isTrussardiLayout
          ? classes.trussardiLayout
          : selectedBrand === "mix"
          ? classes.mixLayout
          : classes.karstenLayout
      }
      style={{
        position: "relative",
        minHeight: "100vh",
        display: mobile ? "block" : "flex",
        backgroundColor: brandColors.bg,
        color: brandColors.textBase,
        fontFamily: "'Alexandria', sans-serif",
        ...(mobile && {
          paddingBottom: "120px",
        }),
      }}
    >
      <BrandSelector
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        availableBrands={availableBrands}
        mobile={mobile}
      />

      <main
        className="main-content"
        style={{
          width: "100%",
          padding: mobile ? "16px" : "32px",
        }}
      >
        <VitrineHeader
          selectedBrand={searching ? "mix": selectedBrand}
          brandColors={brandColors}
          data={data}
          mobile={mobile}
        />

        <ErrorMessages
          openMessageError={openMessageError}
          finishOrder={finishOrder}
        />

        {!finishOrder && !openMessageError && (
          <>
            <PersonalDataForm
              open={personalData}
              onClose={() => setPersonalData(false)}
              nameInput={nameInput}
              setNameInput={setNameInput}
              cpfInput={cpfInput}
              setCpfInput={setCpfInput}
              error={personalDataError}
              onSubmit={handlePersonalDataSubmit}
              brandColors={brandColors}
            />
            {!personalData && searching ? (
              <div style={{ textAlign: "center", padding: "24px" }}>
                <p>Buscando informações...</p>
                <div
                  style={{
                    width: "100%",
                    height: "4px",
                    backgroundColor: brandColors.border,
                    borderRadius: "2px",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "30%",
                      height: "100%",
                      backgroundColor: brandColors.primary,
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            ) : !data || !data.products || data.products.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "48px 24px",
                  color: brandColors.textMuted,
                }}
              >
                <p style={{ fontSize: mobile ? "16px" : "18px", margin: 0 }}>
                  Nenhum produto disponível no momento.
                </p>
              </div>
            ) : (
              <div
                className="product-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: mobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: mobile ? "16px" : "24px",
                }}
              >
                {data.products
                  .filter((product) => {
                    if (selectedBrand === "mix") {
                      return true;
                    }

                    if (selectedBrand === "karsten") {
                      return product.marcaId === 1 || product.marcaId === 3;
                    }

                    if (selectedBrand === "trussardi") {
                      return product.marcaId === 2;
                    }
                    return true;
                  })
                  .map((product) => (
                    <ProductCard
                      key={product.idProduto}
                      product={product}
                      brandColors={brandColors}
                      selectedBrand={selectedBrand}
                      quantity={productQuantities[product.idProduto] || 1}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={addToCart}
                      onShowSpecs={handleShowSpecs}
                      onCarouselScroll={handleCarouselScroll}
                    />
                  ))}
              </div>
            )}
          </>
        )}
      </main>

      <CartSidebar
        isCartOpen={isCartOpen}
        setIsCartOpen={setIsCartOpen}
        cartDetails={cartDetails}
        brandColors={brandColors}
        selectedBrand={selectedBrand}
        subtotal={subtotal}
        total={total}
        discount={discount}
        onRemoveFromCart={removeFromCart}
        onUpdateCartQuantity={updateCartQuantity}
        onFinishOrder={handleFinishOrder}
      />

      <CartButton
        onClick={() => setIsCartOpen(!isCartOpen)}
        totalItems={totalItems}
        brandColors={brandColors}
      />

      <SpecsModal
        open={openSpecsModal}
        product={specsProduct}
        onClose={() => setOpenSpecsModal(false)}
        brandColors={brandColors}
        selectedBrand={selectedBrand}
      />

      <ConfirmationModal
        open={openConfirmationModal}
        onClose={() => {
          setOpenConfirmationModal(false);
          setIsCartOpen(false);
        }}
        brandColors={brandColors}
      />

      {openDetail && (
        <DialogProductDetailShowcaseClient
          storeId={data?.storeId}
          detail={productDetail}
          open={openDetail}
          handleClose={() => {
            setProductDetail(null);
            setOpenDetail(false);
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        style={{
          bottom: mobile ? "100px" : "120px",
        }}
      />
    </div>
  );
}
